import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Upload, ScanLine, Loader2, CheckCircle, FileImage, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { casesAPI } from '../lib/api';
import { FieldRow, ListRow } from './ScannerRows';

const FORMS = ['I-9','I-130','I-131','I-140','I-485','I-539','I-589','I-693','I-765','N-400','N-600','DS-160','DS-260','G-325A','G-28','G-639','G-1145'];
const TABS = { personal:'Personal', contact:'Contact', immigration:'Immigration', employment:'Employment', education:'Education', emergency:'Emergency', other:'Other' };

function groupByTab(arr) {
  var g = {};
  for (var i = 0; i < arr.length; i++) {
    var t = arr[i].tab || 'other';
    if (!g[t]) g[t] = [];
    g[t].push(arr[i]);
  }
  return g;
}

function buildRows(newFields, conflictFields, selectedFields, toggleFn) {
  var rows = [];
  for (var i = 0; i < newFields.length; i++) {
    var f = newFields[i];
    rows.push({ key: f.field, field: f.field, label: f.label, current: '', extracted: f.extracted, kind: 'new', checked: !!selectedFields[f.field] });
  }
  for (var j = 0; j < conflictFields.length; j++) {
    var c = conflictFields[j];
    rows.push({ key: c.field, field: c.field, label: c.label, current: c.current, extracted: c.extracted, kind: 'conflict', checked: !!selectedFields[c.field] });
  }
  return rows;
}

function UploadView({ formType, setFormType, fileRef, onScan, scanError }) {
  return (
    <Card className="border-dashed" data-testid="enhanced-scanner">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
          <ScanLine className="w-5 h-5 text-primary" />
          Extract Data from Filled Form
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="font-body text-xs text-muted-foreground mb-2">Form Type (optional)</p>
          <select value={formType} onChange={function(e){setFormType(e.target.value);}} className="w-full h-9 rounded-md border border-input bg-card px-3 text-sm" data-testid="form-type-select">
            <option value="">Auto-detect</option>
            {FORMS.map(function(f){return <option key={f} value={f}>{f}</option>;})}
          </select>
        </div>
        <div className="text-center py-4">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onScan} className="hidden" data-testid="enhanced-scanner-input" />
          <FileImage className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-body text-sm text-muted-foreground mb-3">Upload a filled immigration form to extract data with AI</p>
          <Button variant="outline" onClick={function(){if(fileRef.current) fileRef.current.click();}} data-testid="enhanced-scanner-upload-btn">
            <Upload className="w-4 h-4 mr-2" />Upload Filled Form
          </Button>
        </div>
        {scanError ? <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 flex items-start gap-2"><AlertCircle className="w-4 h-4 text-red-500 mt-0.5" /><p className="font-body text-sm text-red-700 dark:text-red-400">{scanError}</p></div> : null}
      </CardContent>
    </Card>
  );
}

function ScanningView() {
  return (
    <Card data-testid="enhanced-scanner-scanning">
      <CardContent className="py-12 text-center">
        <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
        <p className="font-heading font-semibold text-foreground">Analyzing Form with AI...</p>
        <p className="font-body text-sm text-muted-foreground mt-1">Extracting data from every page</p>
      </CardContent>
    </Card>
  );
}

function TabBlock({ tab, rows, open, onToggle, toggleField, conflictCount }) {
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button className="w-full flex items-center gap-2 px-4 py-2.5 bg-muted/30 hover:bg-muted/50" onClick={onToggle} data-testid={'tab-'+tab}>
        <span className="font-body text-sm font-medium">{TABS[tab] || tab}</span>
        <Badge variant="secondary" className="text-[10px]">{rows.length}</Badge>
        {conflictCount > 0 ? <Badge className="text-[10px] bg-amber-500/15 text-amber-600 border-amber-500/20 hover:bg-amber-500/15">{conflictCount} conflicts</Badge> : null}
      </button>
      {open ? <div className="divide-y divide-border/50">
        {rows.map(function(r){
          return <FieldRow key={r.key} field={r.field} label={r.label} current={r.current} extracted={r.extracted} kind={r.kind} checked={r.checked} onCheck={function(){toggleField(r.field, r.extracted);}} testId={'field-'+r.field} />;
        })}
      </div> : null}
    </div>
  );
}

export default function EnhancedFormScanner({ caseId, onDataApplied }) {
  var phaseState = useState('upload');
  var phase = phaseState[0]; var setPhase = phaseState[1];
  var ftState = useState('');
  var formType = ftState[0]; var setFormType = ftState[1];
  var resState = useState(null);
  var result = resState[0]; var setResult = resState[1];
  var errState = useState('');
  var scanError = errState[0]; var setScanError = errState[1];
  var selState = useState({});
  var sel = selState[0]; var setSel = selState[1];
  var slState = useState({});
  var selLists = slState[0]; var setSelLists = slState[1];
  var appState = useState(false);
  var applying = appState[0]; var setApplying = appState[1];
  var otState = useState({});
  var openTabs = otState[0]; var setOpenTabs = otState[1];
  var fileRef = useRef(null);

  function scan(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 10485760) { toast.error('File too large'); return; }
    setPhase('scanning');
    setScanError('');
    var ft = formType && formType !== 'auto' ? formType : undefined;
    casesAPI.extractFormIntoCase(caseId, file, ft).then(function(r) {
      var d = r.data;
      setResult(d);
      var s = {};
      var nf = d.new_fields || [];
      for (var i = 0; i < nf.length; i++) s[nf[i].field] = nf[i].extracted;
      var cf = d.conflicts || [];
      for (var j = 0; j < cf.length; j++) s[cf[j].field] = cf[j].extracted;
      setSel(s);
      var sl = {};
      var ld = d.list_data || [];
      for (var k = 0; k < ld.length; k++) sl[ld[k].field] = ld[k].extracted;
      setSelLists(sl);
      var t = {};
      for (var m = 0; m < nf.length; m++) t[nf[m].tab] = true;
      for (var n = 0; n < cf.length; n++) t[cf[n].tab] = true;
      setOpenTabs(t);
      setPhase('results');
      toast.success('Extraction complete');
    }).catch(function(err) {
      var msg = 'Extraction failed';
      if (err.response && err.response.data && err.response.data.detail) msg = err.response.data.detail;
      setScanError(msg);
      toast.error(msg);
      setPhase('upload');
    });
  }

  function toggleField(field, val) {
    setSel(function(p) { var n = Object.assign({}, p); if (n[field]) delete n[field]; else n[field] = val; return n; });
  }

  function toggleListItem(field, val) {
    setSelLists(function(p) { var n = Object.assign({}, p); if (n[field]) delete n[field]; else n[field] = val; return n; });
  }

  function apply() {
    setApplying(true);
    casesAPI.applyExtractedData(caseId, { fields: sel, lists: selLists }).then(function() {
      toast.success('Data applied');
      if (onDataApplied) onDataApplied();
      doReset();
    }).catch(function() {
      toast.error('Failed to apply');
    }).finally(function() {
      setApplying(false);
    });
  }

  function doReset() {
    setPhase('upload'); setResult(null); setScanError(''); setSel({}); setSelLists({});
    if (fileRef.current) fileRef.current.value = '';
  }

  if (phase === 'scanning') return <ScanningView />;
  if (phase === 'upload') return <UploadView formType={formType} setFormType={setFormType} fileRef={fileRef} onScan={scan} scanError={scanError} />;

  // Results
  var newFields = result.new_fields || [];
  var conflicts = result.conflicts || [];
  var ng = groupByTab(newFields);
  var cg = groupByTab(conflicts);
  var tabKeys = Object.keys(ng).concat(Object.keys(cg)).filter(function(v,i,a){return a.indexOf(v)===i;});
  var cnt = Object.keys(sel).length + Object.keys(selLists).length;
  var listData = result.list_data || [];
  var unchanged = result.unchanged || [];
  var summary = result.summary || {};

  return (
    <Card data-testid="enhanced-scanner-results">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" /> Extraction Results
            </CardTitle>
            <p className="font-body text-xs text-muted-foreground mt-1">
              {summary.new || 0} new, {summary.conflicts || 0} conflicts, {summary.unchanged || 0} unchanged
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={doReset}><X className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
        {tabKeys.map(function(tab) {
          var tabNewArr = ng[tab] || [];
          var tabConArr = cg[tab] || [];
          var rows = buildRows(tabNewArr, tabConArr, sel, toggleField);
          var isOpen = openTabs[tab] !== false;
          return <TabBlock key={tab} tab={tab} rows={rows} open={isOpen} onToggle={function(){setOpenTabs(function(p){var n=Object.assign({},p);n[tab]=!isOpen;return n;});}} toggleField={toggleField} conflictCount={tabConArr.length} />;
        })}

        {listData.length > 0 ? <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-muted/30 font-body text-sm font-medium">List Data</div>
          {listData.map(function(l) {
            return <ListRow key={l.field} field={l.field} label={l.label} count={l.extracted_count} checked={!!selLists[l.field]} onCheck={function(){toggleListItem(l.field, l.extracted);}} />;
          })}
        </div> : null}

        {unchanged.length > 0 ? <p className="font-body text-xs text-muted-foreground text-center">{unchanged.length} fields matched</p> : null}

        <div className="flex gap-2 pt-2">
          <Button onClick={apply} disabled={applying || cnt===0} className="flex-1" data-testid="apply-extracted-btn">
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {applying ? 'Applying...' : 'Apply '+cnt+' Selected'}
          </Button>
          <Button variant="outline" onClick={function(){if(fileRef.current) fileRef.current.click();}}>
            <Upload className="w-4 h-4 mr-2" />Scan Another
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
